import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  RefreshCcw,
  TrendingUp,
  MousePointerClick,
  Eye,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/summary");
      setData(data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Performance
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Impressions, clicks and CTR per sponsored video rule, plus a breakdown of
            favorites by category.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="analytics-refresh-btn"
          className="h-10"
        >
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 stagger">
            <Kpi
              testId="kpi-impressions"
              icon={Eye}
              label="Impressions"
              value={data.totals.impressions}
              tone="primary"
            />
            <Kpi
              testId="kpi-clicks"
              icon={MousePointerClick}
              label="Clicks"
              value={data.totals.clicks}
            />
            <Kpi
              testId="kpi-favorites"
              icon={Heart}
              label="Favorites total"
              value={data.totals.favorites}
            />
          </div>

          {/* Rules performance */}
          <section className="bg-card border border-border rounded-2xl p-6 mb-6">
            <div className="text-sm font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" /> Video ad performance
            </div>
            {data.rules_performance.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No ad events yet. Mobile app can POST to{" "}
                <span className="mono">/api/analytics/track</span> with{" "}
                <span className="mono">{"{event, rule_id, category}"}</span>.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="rules-perf-table">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left py-2 font-semibold">Rule</th>
                    <th className="text-right py-2 font-semibold">Impressions</th>
                    <th className="text-right py-2 font-semibold">Clicks</th>
                    <th className="text-right py-2 font-semibold">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rules_performance.map((r) => (
                    <tr key={r.rule_id} className="border-t border-border">
                      <td className="py-3 mono text-xs">{r.rule_id}</td>
                      <td className="py-3 text-right font-semibold">
                        {r.impressions}
                      </td>
                      <td className="py-3 text-right font-semibold">{r.clicks}</td>
                      <td className="py-3 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-accent text-accent-foreground text-xs font-bold">
                          {r.ctr}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Favorites by category chart */}
          <section className="bg-card border border-border rounded-2xl p-6">
            <div className="text-sm font-bold mb-4 flex items-center gap-2">
              <Heart className="w-4 h-4 text-primary" /> Favorites by category
            </div>
            {data.favorites_by_category.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No favorites yet.
              </div>
            ) : (
              <div className="w-full" style={{ height: 320 }} data-testid="fav-chart">
                <ResponsiveContainer>
                  <BarChart data={data.favorites_by_category}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(30 18% 90%)" />
                    <XAxis
                      dataKey="category"
                      tick={{ fontSize: 11 }}
                      angle={-25}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid hsl(30 18% 90%)",
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(24 100% 50%)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const Kpi = ({ label, value, icon: Icon, testId, tone }) => (
  <div
    data-testid={testId}
    className="bg-card border border-border rounded-2xl p-6"
  >
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
        tone === "primary"
          ? "bg-primary text-primary-foreground"
          : "bg-secondary text-secondary-foreground"
      }`}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-extrabold tracking-tight leading-none mb-1.5">
      {value}
    </div>
    <div className="text-sm font-medium text-muted-foreground">{label}</div>
  </div>
);
